<script setup lang="ts">
import axios from "axios";
import { computed, defineAsyncComponent, onMounted, ref } from "vue";
import {
    applyUniqueKeyToObject,
    ensureUniqueKey,
} from "../../../shared/unique-identifier-key";
import {
    getPaymentMethodLogos,
    getViaBillLogo,
} from "../../controls/checkout/payment-type/image-urls";
import {
    type PaymentLogoKey,
    type PaymentMethodKey,
} from "../../controls/checkout/payment-type/payment-method";
import PaymentCardItem from "../../controls/frontend-productpage/shop/PaymentCardItem.vue";
import { type TrustpilotData } from "../../controls/header/top-site/trustpilot-data";
import Filler from "../../controls/shared/Filler.vue";
import { type IconKey } from "./async-icons";
import IconComponent from "./icon.vue";
import PaymentImage from "../../controls/checkout/shop-checkout/payment-image.vue";
const trustpilotBanner = defineAsyncComponent(
    () =>
        import("../../controls/header/trustpilot-banner/trustpilot-banner.vue")
);

const paymentKeys = ref<(PaymentLogoKey | PaymentMethodKey)[]>([
    "CreditCard",
    "MobilePay",
    "PayEx",
    "visa",
    "mastercard",
    "dankort",
]);

const cardPaymentLogos = computed(() => {
    return getPaymentMethodLogos(paymentKeys.value);
});

type MenuItem = {
    href: string;
    target: string | undefined | null;
    text: string;
    items?: MenuItem[] | null | undefined;
};

type FooterData = {
    followHeader: string;
    leftPartContent: string;
    menuItemsHeader: string;
    menuItems: MenuItem[];
    menuItems2: MenuItem[];
    menuItems3: MenuItem[];
    apps: {
        image: string;
        link: string;
    }[];
    followLinks: {
        name: string;
        link: string;
        icon: IconKey;
    }[];
    formula?:
        | null
        | undefined
        | {
              header: string;
              subText: string;
              name: string;
              namePlaceholder: string | undefined | null;
              email: string;
              emailPlaceholder: string | undefined | null;
              submitText: string;
          };
    trustpilot?: TrustpilotData | undefined;
};

const footerDataRequest = axios
    .create({
        adapter: "fetch",
        fetchOptions: {
            priority: "low",
        },
    })
    .get<FooterData>("/api/footer", {
        params: {
            v: window.odendo.staticFileVersion,
        },
    });

const data = ref<FooterData | null>(null);

function* flatten<T>(
    arr: T[],
    selector: (item: T) => T[] | undefined | null
): Generator<T> {
    for (const item of arr) {
        yield item;
        const subItems = selector(item);
        if (subItems != null) {
            for (const subItem of flatten(subItems, selector)) {
                yield subItem;
            }
        }
    }
}

onMounted(async () => {
    const newData = (await footerDataRequest).data;
    data.value = newData;
    for (const item of flatten(newData.menuItems, (x) => x.items)) {
        Object.assign(applyUniqueKeyToObject(item), { type: "menu-item" });
    }
    menuItems.value = newData.menuItems as MenuItemWithType[];
});

function* repeat<T>(v: T, count: number, copy = false) {
    for (let i = 0; i < count; i++) {
        if (copy) {
            yield structuredClone(v);
        } else {
            yield v;
        }
    }
}

type FillerItemWithType = { type: "filler" };
type MenuItemWithType = MenuItem & { type: "menu-item" };
type MenuItemsItem = FillerItemWithType | MenuItemWithType;

const menuItems = ref<MenuItemsItem[]>(
    Array.from(repeat({ type: "filler" } as FillerItemWithType, 10, true)).map(
        applyUniqueKeyToObject
    )
);

type GroupItem = {
    header?: string | null | undefined;
    items: MenuItemsItem[];
};

const navItemGroups = computed(() => {
    if (
        menuItems.value.some(
            (x) =>
                x.type == "filler" ||
                (x.href != null && x.href.trim().length > 0)
        )
    ) {
        return [
            applyUniqueKeyToObject({
                header: data.value?.menuItemsHeader,
                items: menuItems.value,
            } as GroupItem),
        ];
    }
    const result: GroupItem[] = [];
    for (const groupMenu of menuItems.value) {
        if (groupMenu.type == "filler") {
            continue;
        }
        if (groupMenu.items == null) continue;
        result.push(
            applyUniqueKeyToObject({
                header: groupMenu.text,
                items: groupMenu.items,
            } as GroupItem)
        );
    }
    return result;
});

const formulaLoading = ref(false);

const name = ref("");
const email = ref("");

type ErrorData = {
    errorField?: string;
    reason?: string;
};

type SubResult =
    | {
          success: true;
          message: string;
      }
    | ({
          success: false;
      } & ErrorData);

const formulaError = ref<ErrorData | null>(null);
const formulaSet = ref(false);
const formulaHeight = ref(0);
const formulaReply = ref("");

async function submitFormula(event: Event) {
    if (!(event instanceof SubmitEvent)) return;
    if (formulaLoading.value == true) return;
    formulaLoading.value = true;
    try {
        formulaError.value = null;
        const form =
            event.target != null
                ? event.target instanceof HTMLFormElement
                    ? event.target
                    : "form" in event.target
                    ? (event.target.form as HTMLFormElement)
                    : undefined
                : undefined;
        if (form == null) throw new Error("Form error");
        const action = form.action;
        const method = form.method;
        if (method.toLowerCase() != "post") throw new Error("Form error");
        const response = await axios.post<SubResult>(action, {
            name: name.value,
            email: email.value,
        });
        const result = response.data;
        if (!result.success) {
            formulaError.value = result;
        } else {
            formulaHeight.value = form.offsetHeight;
            formulaSet.value = true;
            if (result.message != null && result.message != "") {
                formulaReply.value = result.message;
            }
        }
    } catch (e) {
    } finally {
        formulaLoading.value = false;
    }
}
</script>

<template>
    <div class="footer-component">
        <section class="footer-content-width footer-sections">
            <div class="left-part-element">
                <div
                    class="left-aligned-text text-style-1"
                    style="white-space: break-spaces"
                >
                    <template v-if="data == null">
                        <Filler :min="150" :max="200"></Filler>
                        &nbsp;
                        <Filler :min="80" :max="120"></Filler>
                        &nbsp;
                        <Filler :min="150" :max="190"></Filler>
                    </template>
                    <div v-html="data.leftPartContent" v-else></div>
                </div>

                <div
                    class="follow-links"
                    v-if="data != null && data.followLinks.length > 0"
                >
                    <div class="follow-header">
                        <strong>{{ data.followHeader }}</strong>
                    </div>
                    <div class="follow-icons">
                        <a
                            v-for="followLink in data.followLinks"
                            class="follow-icon"
                            :aria-label="'Følg os på ' + followLink.name"
                            :href="followLink.link"
                            target="_blank"
                        >
                            <iconComponent
                                class="icon-comp"
                                :value="followLink.icon"
                                color="white"
                            ></iconComponent>
                        </a>
                    </div>
                </div>
            </div>

            <div class="navigation-part">
                <nav class="navigation-items">
                    <ol
                        class="footer-items"
                        v-for="navItemGroup in navItemGroups"
                        :key="ensureUniqueKey(navItemGroup)"
                    >
                        <li
                            v-if="navItemGroup.header != null"
                            class="navigation-header"
                        >
                            {{ navItemGroup.header }}
                        </li>
                        <li
                            v-for="item in navItemGroup.items"
                            :key="ensureUniqueKey(item)"
                        >
                            <a
                                :href="item.type === 'filler' ? '#' : item.href"
                                :target="
                                    item.type == 'menu-item' &&
                                    item.target != null
                                        ? item.target
                                        : ''
                                "
                            >
                                <Filler
                                    v-if="item.type === 'filler'"
                                    :min="10"
                                    :max="20"
                                ></Filler>
                                <template v-else>{{ item.text }}</template>
                            </a>
                        </li>
                    </ol>
                </nav>
            </div>

            <div
                :class="{
                    'formula-part': true,
                    'formula-part--sent': formulaSet,
                }"
            >
                <form
                    v-if="formulaSet != true"
                    class="formula"
                    action="/api/footer/subscribe-newsletter"
                    method="post"
                    @submit.prevent="submitFormula($event)"
                >
                    <div
                        class="formula-error-row"
                        v-if="
                            formulaError != null &&
                            formulaError.errorField == null &&
                            formulaError.reason != null
                        "
                    >
                        {{ formulaError?.reason }}
                    </div>
                    <fieldset
                        :disable="formulaLoading"
                        v-if="data == null || data.formula != null"
                    >
                        <div class="formula-block">
                            <div class="formula-header">
                                <div>
                                    <strong>
                                        <Filler
                                            v-if="data == null"
                                            :min="23"
                                            :max="23"
                                        ></Filler>
                                        <template v-else>{{
                                            data.formula?.header
                                        }}</template>
                                    </strong>
                                </div>
                            </div>
                            <div class="formula-subtext">
                                <p class="left-aligned-text text-style-1">
                                    <Filler
                                        v-if="data == null"
                                        :min="69"
                                        :max="69"
                                    ></Filler>
                                    <template v-else>{{
                                        data.formula?.subText
                                    }}</template>
                                </p>
                            </div>
                        </div>

                        <div class="formula-row">
                            <div class="formula-col">
                                <div
                                    class="formula-error-row"
                                    v-if="
                                        formulaError != null &&
                                        formulaError.errorField == 'name' &&
                                        formulaError.reason != null
                                    "
                                >
                                    {{ formulaError?.reason }}
                                </div>
                                <div
                                    v-if="data?.formula?.name != null"
                                    class="formula-field-header"
                                >
                                    {{ data.formula.name }}
                                </div>
                                <input
                                    type="text"
                                    autocomplete="name"
                                    id="formula-field-name"
                                    required
                                    :class="{
                                        'text-field': true,
                                        'text-field-error':
                                            formulaError?.errorField == 'name',
                                    }"
                                    v-model="name"
                                    :placeholder="
                                        data?.formula?.namePlaceholder ??
                                        undefined
                                    "
                                />
                            </div>

                            <div class="formula-col">
                                <div
                                    class="formula-error-row"
                                    v-if="
                                        formulaError != null &&
                                        formulaError.errorField == 'email' &&
                                        formulaError.reason != null
                                    "
                                >
                                    {{ formulaError?.reason }}
                                </div>
                                <div
                                    v-if="data?.formula?.email != null"
                                    class="formula-field-header"
                                >
                                    {{ data.formula.email }}
                                </div>
                                <input
                                    type="email"
                                    autocomplete="email"
                                    id="formula-field-email"
                                    required
                                    :class="{
                                        'text-field': true,
                                        'text-field-error':
                                            formulaError?.errorField == 'email',
                                    }"
                                    v-model="email"
                                    :placeholder="
                                        data?.formula?.emailPlaceholder ??
                                        undefined
                                    "
                                />
                            </div>
                        </div>

                        <div class="formula-block">
                            <button
                                type="submit"
                                class="button"
                                data-at="button"
                            >
                                {{ data?.formula?.submitText }}
                            </button>
                        </div>
                    </fieldset>
                </form>
                <div
                    v-else
                    class="filler-formula-sent"
                    :style="{
                        height:
                            formulaHeight > 0
                                ? `${formulaHeight}px`
                                : undefined,
                    }"
                >
                    {{ formulaReply }}
                </div>

                <div class="payment-methods">
                    <template v-if="cardPaymentLogos != null">
                        <template v-for="cardPaymentLogo in cardPaymentLogos">
                            <PaymentCardItem
                                :card-payment-logo="cardPaymentLogo"
                                lazy
                            />
                        </template>
                    </template>
                    <div
                        class="viabill-section"
                        v-if="paymentKeys?.includes('PayEx') == true"
                    >
                        <PaymentImage
                            :image-data="getViaBillLogo()"
                            lazy
                            alt="ViaBill logo"
                        ></PaymentImage>
                    </div>
                </div>

                <template v-if="data?.trustpilot">
                    <br />
                    <trustpilot-banner
                        :model-value="data.trustpilot"
                        inverted-colors
                    ></trustpilot-banner>
                </template>

                <div class="apps" v-if="data != null">
                    <div v-for="app in data.apps" class="app-item">
                        <a :href="app.link" class="app-link" target="_blank">
                            <img class="app-image" alt="" :src="app.image" />
                        </a>
                    </div>
                </div>
            </div>
        </section>
        <section class="bottom-part text-center">
            <div class="bottom-info">
                <template v-if="data != null">
                    <template
                        v-for="{ item, i } in data.menuItems2.map(
                            (item, i) => ({ item, i })
                        )"
                    >
                        <template v-if="i > 0"> - </template>
                        <a
                            class="menu-item-link"
                            :href="item.href"
                            v-if="item.href != null && item.href.trim() != ''"
                            >{{ item.text }}</a
                        >
                        <span class="menu-item-link" v-else>{{
                            item.text
                        }}</span>
                    </template>
                </template>
            </div>

            <div class="sub-bottom-info">
                <template v-if="data != null">
                    <template
                        v-for="{ item, i } in data.menuItems3.map(
                            (item, i) => ({ item, i })
                        )"
                    >
                        <template v-if="i > 0"> - </template>
                        <a
                            class="menu-item-link"
                            :href="item.href"
                            v-if="item.href != null && item.href.trim() != ''"
                            >{{ item.text }}</a
                        >
                        <span class="menu-item-link" v-else>{{
                            item.text
                        }}</span>
                    </template>
                </template>
            </div>
        </section>
    </div>
</template>

<style lang="scss" scoped>
@import "../../../responsive/style.scss";

.footer-component {
    -webkit-font-smoothing: antialiased;
    margin: 0px;
    width: 100%;
    max-width: 100vw;
    font-weight: 400;
    background: rgb(255, 255, 255);
    color: white;
    background-color: black;
    padding: 30px;
    box-sizing: border-box;
}

a {
    text-decoration: none;
    color: inherit;
}

a:not(.footer-btn):hover {
    text-decoration: underline;
}

p {
    margin: 0px;
    font-size: inherit;
    font-weight: inherit;
}

p {
    overflow-wrap: break-word;
}

.button {
    background: #00b7b5 !important;
    color: #000000;
    font-size: 10.5pt;
    font-weight: 700;
    height: 2.5rem;
    width: 100%;
    border-radius: 3px;
    border: none;
    cursor: pointer;
}

.navigation-header {
    text-decoration: underline;
    cursor: default;
}

.navigation-items li {
    margin-top: 0.35rem;
    a {
        padding-top: 4px;
        padding-bottom: 4px;
    }
}

.left-aligned-text {
    text-align: left;
}

.formula-header {
    font-size: 12pt;
    text-align: left;
}

.formula-subtext {
    text-align: left;
}

[class*="line-"] {
    box-sizing: content-box;
}

.text-center {
    text-align: center;
}

.sub-bottom-info {
    margin-top: 15px;
    font-size: 7pt;
    line-height: 1rem;
}

.menu-item-link {
    white-space: pre;
}

.bottom-part {
    @include media-breakpoint-up(lg) {
        margin-top: 40px;
    }
}

.text-style-1 {
    line-height: 1.3125rem;
}

.text-field {
    border: 0.0625rem solid #a3bac6;
    border-radius: 0.1875rem;
    background: #f0f3f5;
    height: 2.5rem;
    width: 100%;
    padding: 20px 10px;
    box-sizing: border-box;
}

.payment-methods {
    display: flex;
    align-items: center;
    margin-top: 10px;
    flex-wrap: wrap;
    justify-content: center;

    @include media-breakpoint-up(lg) {
        flex-wrap: revert;
        justify-content: flex-start;
    }
}

.payment-type-logo-item-frame:first-child {
    margin-left: 0;
}
.payment-type-logo-item-frame:last-child {
    margin-right: 0px;
}

.apps {
    display: flex;
    flex-direction: row;
    margin-top: 20px;
    justify-content: space-between;
    align-items: stretch;
}

.app-item {
    flex: 1;
    margin-right: 20px;
}

.app-item:last-child {
    margin-right: 0;
}

.app-link {
    display: flex;
    border-radius: 0.625rem 0.625rem 0.625rem 0.625rem;
    overflow: hidden;
    box-shadow: 0 0 0px 1px white;
    height: 100%;
    align-items: center;
    justify-content: center;
}

.app-image {
    //height: 2.25rem;
    width: 100%;
}

ol.footer-items {
    list-style: none;
    padding: 0;
    margin: 0;
    margin-bottom: 1em;
}

.footer-sections {
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.footer-content-width {
    width: 100%;
}

$desktop-width: 980px;
$desktop-margin: 30px;

@mixin desktop-part($percent) {
    flex-basis: calc(#{($percent * 100)}% - #{$desktop-margin});
}

.formula-part {
    margin-top: 30px;
    margin-bottom: 20px;
}

.formula-block {
    margin-top: 10px;
    margin-bottom: 10px;
}
.formula-block:first-child {
    margin-top: 0;
}
.formula-block:last-child {
    margin-bottom: 0;
}

.follow-links {
    margin-top: 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
}

.follow-header {
    font-size: 12pt;
    margin-right: 20px;
}

.follow-icons {
    display: flex;
    flex-direction: row;
    align-items: center;
}

.follow-icon {
    display: block;
    border: 1px solid white;
    padding: 4px;
    width: 30px;
    height: 30px;
    box-sizing: border-box;
    border-radius: 100%;
    margin-right: 10px;
}
.follow-icon:last-child {
    margin-right: 0;
}

.icon-comp {
    width: 100%;
    height: 100%;
}

.formula-error-row {
    background: rgb(156, 43, 43);
    color: white;
}

.formula-field-header {
    margin-bottom: 0.5rem;
}

.formula-row {
    display: flex;
    flex-direction: row;
}

.formula-col:not(:first-child) {
    margin-left: 5px;
}

.formula-col:not(:last-child) {
    margin-right: 5px;
}

.formula-col {
    flex: 1;
}

.text-field-error {
    background-color: #eb8686;
}

.filler-formula-sent {
    display: flex;
    align-items: center;
    justify-content: center;
}

.navigation-part {
    margin-top: 30px;
}

@include media-breakpoint-up(lg) {
    .navigation-items li {
        margin-top: 0.15rem;
        a {
            padding: 0;
        }
    }

    .footer-sections {
        flex-direction: row;
        align-items: baseline;
        justify-content: space-between;
    }

    .footer-content-width {
        width: $desktop-width;
        margin: auto;
    }

    .left-part-element {
        @include desktop-part(0.45);
    }
    .navigation-part {
        //@include desktop-part(0.25);
        margin-top: 0;
    }
    .formula-part {
        margin-top: 0;
        @include desktop-part(0.3);
        &--sent {
            align-self: start;
        }
    }
    .follow-links {
        margin-top: 30px;
    }
}
</style>
